import { useResponsiveLayout } from './useResponsiveLayout';

/**
 * Hook to detect if the current viewport is mobile/tablet based on a media query.
 * Uses matchMedia for responsive detection (client-side only).
 *
 * This hook is now implemented using the more comprehensive useResponsiveLayout hook
 * for consistency and better performance.
 *
 * @param query - Media query string to test against (default: navigation breakpoint for mobile)
 * @returns boolean indicating if the viewport matches the mobile query
 *
 * @example
 * ```tsx
 * const isMobile = useIsMobile(); // Uses navigation breakpoint (max-width: 767px)
 * const isTablet = useIsMobile("(max-width: 767px)");
 *
 * return (
 *   <div>
 *     {isMobile ? <MobileLayout /> : <DesktopLayout />}
 *   </div>
 * );
 * ```
 */
export const useIsMobile = (query = '(max-width: 767px)'): boolean => {
	// Extract breakpoint from query string
	const breakpointMatch = /max-width:\s*(\d+)px/.exec(query);
	const breakpoint = breakpointMatch?.[1] ? parseInt(breakpointMatch[1], 10) : 768;

	const { isMobile } = useResponsiveLayout(breakpoint);
	return isMobile;
};
